import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Node } from 'slate'
import { Card, CardContent, CardHeader, CardMedia, Icon, cn, makeStyles } from '@cicero/ui'
import { AttributeDoc, PageDoc, PageInboxStatus, PageSchema, useModel } from '../models'
import { PageActionsMenu } from 'editor/tools'
import { AttributeRenderer } from 'forms'

export const PageCard = ({
  attributes,
  attributesInline,
  header,
  page,
  preview,
  quickOpen,
  strikeArchived,
}: {
  attributes?: AttributeDoc[]
  attributesInline?: boolean
  header?: boolean
  page: PageDoc
  preview?: boolean
  quickOpen?: boolean
  strikeArchived?: boolean
}) => {
  const { tileWrapper, tileCardInline, tileCard, tileCardImage, tileSimple, tileClickable, titleArchived, titleIcon, attributesWrapperInline } = useStyles()
  const Pages = useModel(PageSchema)
  const titleClasses = useMemo(() => cn({ [titleArchived]: strikeArchived && page.inbox_status === PageInboxStatus.archived }), [page, strikeArchived])

  return <Link className={tileWrapper} to={PageSchema.getPageLink(page)} onClick={(ev) => {
    if (quickOpen) {
      ev.preventDefault()
      Pages.active$.next(page)
    }
  }}>
    <Card className={cn(tileCard, { [tileClickable]: preview, [tileCardInline]: attributesInline, [tileSimple]: !header })} variant="outlined">
      { header
        ? <>
          { page.main_image ? <CardMedia className={tileCardImage} image={page.main_image} /> : null }
          <CardHeader
            className={titleClasses}
            title={page.title?.toString()}
            actions={<PageActionsMenu page={page} />}
          />
        </>
        : <span className={titleClasses}>
          { page.icon ? <Icon className={titleIcon}>{page.icon}</Icon> : null }
          { page.title }
        </span>
      }
      { preview && page.content ? <PageContentPreview page={page} /> : null }
      {
        attributes && attributes.length
          ? <span className={cn({ [attributesWrapperInline]: attributesInline })}>
            { attributes.map(attr => <AttributeRenderer key={attr.id} attribute={attr} page={page} compact />) }
          </span>
          : null
      }
    </Card>
  </Link>
}

export const PageContentPreview = ({ page }: { page: PageDoc }) => {
  const text = useMemo(() => {
    const { content } = page
    if (!content) { return '' }

    let str = '', i = 0

    while (str.length < 250 && i < content.length) {
      const node = content[i]
      const el = Node.string(node)

      if (str !== '') { str += ' ' }
      str += el
      i++
    }

    if (str.length > 250) {
      str = str.slice(0, 240) + '...'
    }

    return str
  }, [page.content])

  return text && text.length ? <CardContent>{text}</CardContent> : null
}

const useStyles = makeStyles({
  tileWrapper: {
    display: 'block',
    margin: '4px 0',
    textDecoration: 'none',
  },
  tileClickable: {
    transition: 'all ease-in-out .1s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  titleArchived: {
    textDecoration: 'line-through',
  },
  titleIcon: {
    color: '#AAA',
    fontSize: 16,
    marginRight: 6,
    verticalAlign: 'sub',
  },
  tileSimple: {
    padding: '6px 12px',
  },
  tileCard: {
    // padding: '6px 12px',
  },
  tileCardImage: {
    minHeight: 180,
  },
  tileCardInline: {
    display: 'flex',
    alignItems: 'center',
  },
  attributesWrapper: {

  },
  attributesWrapperInline: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',

    '& > *:not(:empty)': {
      margin: '0px 6px',

      '&:last-of-type': {
        marginRight: 0,
      },
    },
  },
})
